function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import { ValidationApprove, ValidationDecline, EyeOpen, EyeClosed } from '@suranceadmin/icons/native';
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Animated, StyleSheet, TextInput as RNTextInput, Pressable, View } from 'react-native';
import { withTheme } from '../../core/theming';
import ToggleIcon from '../Icon/ToggleIcon';
import Text, { TextType } from '../Typography/Text';
const smallDeviceFontSize = 12;

const animate = (active, fontSizeAnim, translateYAnim, isSmallScreen) => {
  const nextStyle = active ? activeStyle : inActiveStyle;

  if (isSmallScreen) {
    nextStyle.fontSize = active ? smallDeviceFontSize : activeStyle.fontSize;
  }

  Animated.parallel([Animated.timing(fontSizeAnim, {
    toValue: nextStyle.fontSize,
    duration: 200,
    useNativeDriver: false
  }), Animated.timing(translateYAnim, {
    toValue: nextStyle.top,
    duration: 200,
    useNativeDriver: false
  })]).start();
};

const TextInput = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    style,
    value,
    placeholder,
    bottomLabel,
    icon,
    isValid,
    isSmallScreen = false,
    secureTextEntry = false,
    theme: {
      fonts,
      colors,
      isRTL
    },
    onChangeText: onChangeTextProps,
    onBlur: onBlurProps,
    onFocus: onFocusProps,
    onIconPress,
    bottomLabelAccessibility,
    secureToggleAccessibility
  } = props;
  const [isActive, setIsActive] = useState(false);
  const [isSecureTextRevealed, setIsSecureTextRevealed] = useState(!secureTextEntry);
  const RNTextInputRef = useRef(null);
  const translateYAnim = useRef(new Animated.Value(activeStyle.top)).current;
  const fontSizeAnim = useRef(new Animated.Value(isSmallScreen ? smallDeviceFontSize : activeStyle.fontSize)).current;
  const secureIconRef = useRef(null);
  useImperativeHandle(ref, () => ({
    revealSecureText: isRevealed => {
      if (secureTextEntry) {
        if (secureIconRef !== null && secureIconRef !== void 0 && secureIconRef.current) {
          secureIconRef.current.updateIcon(isRevealed);
        }

        setIsSecureTextRevealed(isRevealed);
      }
    }
  }));
  useEffect(() => {
    var _RNTextInputRef$curre;

    const isFocused = RNTextInputRef === null || RNTextInputRef === void 0 ? void 0 : (_RNTextInputRef$curre = RNTextInputRef.current) === null || _RNTextInputRef$curre === void 0 ? void 0 : _RNTextInputRef$curre.isFocused();

    if (!isFocused) {
      animate(!!value, fontSizeAnim, translateYAnim, isSmallScreen);
    }
  }, [value, translateYAnim, fontSizeAnim, isSmallScreen]);
  useEffect(() => {
    setIsSecureTextRevealed(!secureTextEntry);
  }, [secureTextEntry]);
  useEffect(() => {
    if (isValid === false && secureTextEntry === true) {
      setIsSecureTextRevealed(false);
    }
  }, [isValid, secureTextEntry]);

  const onFocus = e => {
    animate(true, fontSizeAnim, translateYAnim, isSmallScreen);
    setIsActive(true);

    if (onFocusProps) {
      onFocusProps(e);
    }
  };

  const onBlur = e => {
    setIsActive(false);

    if (!value) {
      animate(false, fontSizeAnim, translateYAnim, isSmallScreen);
    }

    if (onBlurProps) {
      onBlurProps(e);
    }
  };

  const onChangeText = text => {
    if (onChangeTextProps) {
      onChangeTextProps(text);
    }
  };

  const renderPlaceholder = () => {
    return /*#__PURE__*/React.createElement(Animated.Text, {
      accessible: false,
      style: [value || isActive ? fonts.caption2Bold : fonts.body, {
        fontSize: fontSizeAnim,
        color: value || isActive ? colors.text.disabled : colors.text.default,
        transform: [{
          translateY: translateYAnim
        }]
      }, {
        writingDirection: isRTL ? 'rtl' : 'ltr'
      }] // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: is not assignable to type AnimatedProps
      ,
      pointerEvents: "none"
    }, placeholder);
  };

  const renderIcon = () => {
    if ((isValid === null || isValid === undefined) && !icon) {
      return null;
    } else {
      return /*#__PURE__*/React.createElement(View, {
        style: styles.iconView
      }, getIcon());
    }
  };

  const getIcon = () => {
    if (isValid && secureTextEntry) {
      return /*#__PURE__*/React.createElement(ToggleIcon, {
        accessibility: secureToggleAccessibility,
        initIsSelected: isSecureTextRevealed,
        ref: secureIconRef,
        First: EyeOpen,
        Second: EyeClosed,
        onPress: () => {
          setIsSecureTextRevealed(prev => !prev);
          onIconPress && onIconPress();
        }
      });
    }

    let iconToDraw = icon;

    if (isValid) {
      iconToDraw = /*#__PURE__*/React.createElement(ValidationApprove, {
        color: colors.icon.valid
      });
    } else if (isValid === false) {
      iconToDraw = /*#__PURE__*/React.createElement(ValidationDecline, {
        color: colors.icon.warning
      });
    }

    if (onIconPress) {
      return /*#__PURE__*/React.createElement(Pressable, {
        onPress: onIconPress
      }, iconToDraw);
    } else {
      return iconToDraw;
    }
  };

  const renderBottomLabel = () => {
    return /*#__PURE__*/React.createElement(View, {
      style: styles.bottomLabelView
    }, /*#__PURE__*/React.createElement(Text, _extends({}, bottomLabelAccessibility, {
      type: TextType.Caption2,
      color: isValid === false ? colors.text.warning : colors.text.light
    }), bottomLabel));
  };

  const inputStyles = [styles.input, fonts.body, {
    color: isValid === false ? colors.text.warning : colors.text.default
  }, {
    textAlign: isRTL ? 'right' : 'left'
  }];
  return /*#__PURE__*/React.createElement(View, {
    style: style
  }, /*#__PURE__*/React.createElement(View, {
    style: [styles.inputView, {
      borderBottomColor: colors.line.default
    }, props.multiline ? {
      minHeight: 55
    } : {
      height: 55
    }]
  }, /*#__PURE__*/React.createElement(View, {
    style: {
      flex: 1
    }
  }, renderPlaceholder(), /*#__PURE__*/React.createElement(RNTextInput, _extends({}, props, {
    accessibilityLabel: props.accessibilityLabel || placeholder,
    ref: RNTextInputRef,
    style: inputStyles,
    underlineColorAndroid: "transparent",
    secureTextEntry: !isSecureTextRevealed,
    placeholder: "",
    value: value,
    onBlur: onBlur,
    onFocus: onFocus,
    onChangeText: onChangeText
  }))), renderIcon()), bottomLabel && renderBottomLabel());
});
const styles = StyleSheet.create({
  inputView: {
    flexDirection: 'row',
    borderBottomWidth: 1
  },
  input: {
    flex: 1,
    paddingLeft: 0,
    paddingVertical: 0
  },
  iconView: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: '100%',
    paddingEnd: 12,
    paddingBottom: 7,
    alignSelf: 'flex-end'
  },
  bottomLabelView: {
    paddingTop: 8,
    display: 'flex'
  }
});
const inActiveStyle = {
  fontSize: 17,
  top: 23
};
const activeStyle = {
  fontSize: 14,
  top: 0
};
export default withTheme(TextInput);
//# sourceMappingURL=index.js.map